import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { cn } from '../../../../lib/utils';
import { resultsToReset, roundLabel } from '../../../../lib/bracket';
import { bracketFor } from '../../../../lib/stages';
import { setBracketResult } from '../../../../database/tournamentStages';
import { handleFirebaseError } from '../../../../utils/errorHandler';

const nameOf = (playersById, id) => (id ? playersById[id]?.name || 'Removed player' : 'TBD');

const scoreIsValid = (v) =>
  v === '' || (Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= 99);

const BracketResultDialog = ({ open, onOpenChange, tournamentId, stage, match, playersById }) => {
  const structure = bracketFor(stage);
  const [winner, setWinner] = useState(match.winner);
  const [scoreA, setScoreA] = useState(match.score?.[0] ?? '');
  const [scoreB, setScoreB] = useState(match.score?.[1] ?? '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWinner(match.winner);
    setScoreA(match.score?.[0] ?? '');
    setScoreB(match.score?.[1] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, match]);

  const [a, b] = match.players;
  const seedOf = (id) => {
    const i = (stage.participants || []).indexOf(id);
    return i === -1 ? null : i + 1;
  };
  const done = match.status === 'done';
  const resetCount = resultsToReset(structure, stage.results || {}, match.id).length;
  const winnerChanged = !!match.winner && winner !== match.winner;

  const handleSave = async () => {
    setBusy(true);
    try {
      const reset = await setBracketResult(tournamentId, stage.id, match.id, {
        winner,
        scoreA: scoreA === '' ? null : Number(scoreA),
        scoreB: scoreB === '' ? null : Number(scoreB),
      });
      toast.success(
        `Result saved.${reset.length ? ` ${reset.length} later matches were reset.` : ''}`
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not save the result.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    setBusy(true);
    try {
      const reset = await setBracketResult(tournamentId, stage.id, match.id, null);
      toast.success(
        `Result cleared.${reset.length ? ` ${reset.length} later matches were reset.` : ''}`
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('Could not clear the result.', { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {roundLabel(structure, match.side, match.round)} · Match {match.index}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {[a, b].map((id, i) => (
            <button
              key={id ?? `slot-${i}`}
              type="button"
              disabled={!id}
              aria-pressed={winner === id}
              onClick={() => setWinner(id)}
              className={cn(
                'flex h-16 flex-col items-center justify-center gap-1 rounded-md border text-sm',
                winner === id
                  ? 'border-winner bg-winner/10'
                  : 'border-border/60 hover:border-border'
              )}
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Seed {seedOf(id) ?? '—'}
              </span>
              <span className="font-medium">{nameOf(playersById, id)}</span>
            </button>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">{nameOf(playersById, a)} score</span>
            <Input
              type="number"
              min={0}
              max={99}
              value={scoreA}
              onChange={(e) => setScoreA(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">{nameOf(playersById, b)} score</span>
            <Input
              type="number"
              min={0}
              max={99}
              value={scoreB}
              onChange={(e) => setScoreB(e.target.value)}
            />
          </label>
        </div>

        {winnerChanged && resetCount > 0 && (
          <p className="rounded-md border border-warning/40 px-3.5 py-2.5 text-sm text-warning">
            Changing the winner resets {resetCount} later match{resetCount === 1 ? '' : 'es'}.
          </p>
        )}

        <DialogFooter className="sm:justify-between">
          {done && (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              disabled={busy}
              onClick={handleClear}
            >
              Clear result{resetCount > 0 ? ` (resets ${resetCount})` : ''}
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy || !winner || !scoreIsValid(scoreA) || !scoreIsValid(scoreB)}
              onClick={handleSave}
            >
              {busy ? 'Saving…' : 'Save result'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BracketResultDialog;
